import { useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import {
  type CreatePlan,
  Labs,
  ModalDialog,
  Mtds,
  templates,
  useCreatePlanMutation,
  SaveError,
  widgets,
  useGetLabsQuery,
  Contracts,
  useGetContractsQuery,
  SinglePlan,
  useGetOkProgramsQuery,
  useGetPurposesQuery,
  useGetWorkspacesQuery,
  useErrorToast
} from 'prove-common'
import {
  Form,
  validator,
  type RJSFSchema,
  type UiSchema,
  type IChangeEvent
} from 'prove-common/rjsf'
import { type WorkspaceId } from '../../workspaces/workspaces'
import {
  planInheritanceControls,
  activePlanFormData,
  retainInheritedPlanDrafts,
  withPlanInheritanceControls,
  type PlanField
} from '../plan-field-inheritance/PlanFieldInheritance'
import { validPlanContracts } from '../plan-field-inheritance/planFormOptions'

type PlanSchemaOptions = {
  defaultDueDate: string | undefined
  labs: Labs
  contracts: Contracts
  defaultLabId: number | undefined
  contractId: number | undefined
  okPrograms: { id: number; number: string; name: string }[]
  purposes: { id: number; name: string }[] | undefined
  inherited: Record<Exclude<PlanField, 'workspaceId'>, boolean>
  requiresOkProgram: boolean
}

const OK_PROGRAM_PURPOSE_NAME = 'OK-program'

function isOkProgramPurpose(
  purposeId: number | undefined,
  purposes: { id: number; name: string }[] | undefined
): boolean {
  if (purposeId == null || purposes == null) {
    return false
  }

  return purposes.some(
    (purpose) =>
      purpose.id === purposeId && purpose.name === OK_PROGRAM_PURPOSE_NAME
  )
}

const planSchema = ({
  defaultDueDate,
  labs,
  contracts,
  defaultLabId,
  contractId,
  okPrograms,
  purposes,
  inherited,
  requiresOkProgram
}: PlanSchemaOptions): RJSFSchema => {
  const labIdProperty: RJSFSchema = {
    type: 'number',
    title: 'Laboratorium',
    description: 'Velg hvilket laboratorium planen gjelder for',
    oneOf: labs?.map((lab) => ({
      const: lab.id,
      title: lab.name
    }))
  }

  const contractIdProperty: RJSFSchema = {
    type: 'number',
    title: 'Kontrakt',
    description: 'Velg hvilken kontrakt planen gjelder for',
    oneOf: contracts?.flatMap((contract) => ({
      const: contract.id,
      title: contract.title
    }))
  }

  if (contractId !== undefined) {
    contractIdProperty.default = contractId
  }

  if (defaultLabId !== undefined) {
    labIdProperty.default = defaultLabId
  }

  const okProgramIdProperty: RJSFSchema = {
    type: 'number',
    title: 'OK-program',
    description: 'Velg OK-nummer og navn',
    oneOf: okPrograms.map((r) => ({
      const: r.id,
      title: `${r.number} ${r.name}`
    }))
  }

  const purposeIdProperty: RJSFSchema = {
    type: 'number',
    title: 'Hensikt',
    description: 'Velg hensikt for planen',
    oneOf: (purposes ?? []).map((purpose) => ({
      const: purpose.id,
      title: purpose.name
    }))
  }

  return {
    type: 'object',
    properties: {
      description: {
        type: 'string',
        title: 'Tittel',
        description: 'Gi planen en kort og beskrivende tittel'
      },
      due: {
        type: 'string',
        format: 'date',
        title: 'Frist',
        description: 'Når skal planen være ferdig?',
        ...(inherited.due ? {} : { default: defaultDueDate }),
        ...(inherited.due ? { readOnly: true } : {}),
        ...(!inherited.due
          ? {
              formatMinimum: new Date().toISOString().split('T')[0],
              formatMaximum: defaultDueDate
            }
          : {})
      },
      labId: labIdProperty,
      contractId: contractIdProperty,
      contractRef: {
        type: 'string',
        title: 'Referanse til lab',
        description: 'Referanse til hvilket OK-program det hører til',
        maxLength: 50,
        ...(inherited.contractRef ? { readOnly: true } : {})
      },
      purposeId: {
        ...purposeIdProperty,
        ...(inherited.purposeId ? { readOnly: true } : {})
      },
      okProgramId: okProgramIdProperty
    },
    required: [
      ...(!inherited.due ? ['due'] : []),
      'description',
      'contractId',
      ...(requiresOkProgram ? ['okProgramId'] : [])
    ]
  }
}
const planUiSchema = (options: {
  okProgramPlaceholder?: string
  showOkProgramId: boolean
  inherited: Partial<Record<PlanField, boolean>>
}): UiSchema => ({
  'ui:classNames': 'max-w-[832px]',
  'ui:options': {
    allowWrap: true
  },
  due: {
    'ui:widget': 'selectDueDateWidget',
    'ui:disabled': options.inherited.due === true
  },
  labId: {
    'ui:classNames': 'basis-full max-w-initial'
  },
  contractId: {
    'ui:classNames': 'basis-full max-w-initial'
  },
  contractRef: {
    'ui:disabled': options.inherited.contractRef === true
  },
  okProgramId: {
    'ui:classNames': `${options.showOkProgramId ? '' : 'hidden'} basis-full max-w-initial`,
    'ui:placeholder': options.okProgramPlaceholder ?? '',
    'ui:widget': options.showOkProgramId ? undefined : 'hidden'
  },
  purposeId: {
    'ui:classNames': 'basis-full max-w-initial',
    'ui:disabled': options.inherited.purposeId === true
  }
})

type CreatePlanCardProps = {
  isOpen: boolean
  setOpen: (isOpen: boolean) => void
  parentPlan: SinglePlan | undefined
  workspaceId?: WorkspaceId
}

type CreatePlanFormData = Partial<CreatePlan> & { labId?: number }

type CreatePlanSessionState = {
  parentId: number | undefined
  isOpen: boolean
  formData: CreatePlanFormData | undefined
  inherit: Record<Exclude<PlanField, 'workspaceId'>, boolean>
}

export default function CreatePlanModal({
  isOpen,
  setOpen,
  parentPlan,
  workspaceId
}: Readonly<CreatePlanCardProps>) {
  return (
    <CreatePlanModalSession
      key={`${parentPlan?.id ?? 'root'}:${workspaceId ?? ''}:${isOpen ? 'open' : 'closed'}`}
      isOpen={isOpen}
      setOpen={setOpen}
      parentPlan={parentPlan}
      workspaceId={workspaceId}
    />
  )
}

function CreatePlanModalSession({
  isOpen,
  setOpen,
  parentPlan,
  workspaceId
}: Readonly<CreatePlanCardProps>) {
  const navigate = useNavigate()
  const showNavigationError = useErrorToast('åpne den opprettede planen')
  const dismissed = useRef(false)
  const contractId = parentPlan?.contractId
  const contractRef = parentPlan?.contractRef
  const defaultDueDate = parentPlan?.due
  const parentPlanId = parentPlan?.id
  const parentOkProgramId = parentPlan?.okProgramId
  const parentPurposeId = parentPlan?.purposeId
  const { data: labs } = useGetLabsQuery(isOpen)
  const { data: contracts } = useGetContractsQuery(isOpen)
  const { data: okPrograms } = useGetOkProgramsQuery(isOpen)
  const { data: purposes } = useGetPurposesQuery(isOpen)
  const createPlan = useCreatePlanMutation()
  const workspaceQuery = useGetWorkspacesQuery(isOpen && parentPlan == null)
  const allWorkspaces = workspaceQuery.data ?? []
  const [divisionSelection, setDivisionSelection] = useState<number>()
  const [fagseksjonSelection, setFagseksjonSelection] = useState<number>()
  const contextWorkspace = allWorkspaces.find(
    (workspace) => workspace.id === workspaceId
  )
  const divisionId =
    divisionSelection ??
    (contextWorkspace?.kind?.code === 'TILSYNSDIVISJON'
      ? workspaceId
      : undefined)
  const fagseksjonId =
    fagseksjonSelection ??
    (contextWorkspace?.kind?.code === 'FAGSEKSJON' ? workspaceId : undefined)
  const hasPlacement =
    parentPlan != null ||
    (allWorkspaces.some(
      (workspace) =>
        workspace.id === divisionId &&
        workspace.active &&
        workspace.kind?.code === 'TILSYNSDIVISJON'
    ) &&
      allWorkspaces.some(
        (workspace) =>
          workspace.id === fagseksjonId &&
          workspace.active &&
          workspace.kind?.code === 'FAGSEKSJON'
      ))
  const inheritedDefaults = {
    due: parentPlan != null,
    contractRef: parentPlan != null,
    purposeId: parentPlan != null
  }
  const [saveError, setSaveError] = useState<unknown>()

  const defaultLabId = contractId
    ? contracts?.find((c) => c.id === contractId)?.labId
    : undefined
  const parentOkProgram = okPrograms?.find((r) => r.id === parentOkProgramId)
  const parentOkProgramText = parentOkProgram
    ? `${parentOkProgram.number} ${parentOkProgram.name}`
    : undefined
  const parentPurpose = purposes?.find((r) => r.id === parentPurposeId)
  const parentPurposeText =
    parentPurpose?.name ??
    (parentPurposeId == null ? undefined : `Hensikt ${parentPurposeId}`)
  const [sessionState, setSessionState] = useState<{
    form: CreatePlanSessionState
  }>(() => ({
    form: {
      parentId: parentPlanId,
      isOpen,
      formData: undefined,
      inherit: inheritedDefaults
    }
  }))
  const hasNewFormContext =
    sessionState.form.parentId !== parentPlanId ||
    sessionState.form.isOpen !== isOpen
  const isCurrentContext = (form: CreatePlanSessionState) =>
    form.parentId === parentPlanId && form.isOpen === isOpen
  const updateFormData = (
    update:
      | CreatePlanFormData
      | undefined
      | ((
          previous: CreatePlanFormData | undefined
        ) => CreatePlanFormData | undefined)
  ) => {
    setSessionState((current) => {
      const form = isCurrentContext(current.form)
        ? current.form
        : {
            parentId: parentPlanId,
            isOpen,
            formData: undefined,
            inherit: inheritedDefaults
          }
      return {
        ...current,
        form: {
          ...form,
          formData:
            typeof update === 'function' ? update(form.formData) : update
        }
      }
    })
  }
  const resetFormSession = () => {
    setSessionState({
      form: {
        parentId: parentPlanId,
        isOpen: false,
        formData: undefined,
        inherit: inheritedDefaults
      }
    })
  }
  const updateInheritance = (
    field: Exclude<PlanField, 'workspaceId'>,
    checked: boolean
  ) => {
    setSessionState((current) => {
      const form = isCurrentContext(current.form)
        ? current.form
        : {
            parentId: parentPlanId,
            isOpen,
            formData: undefined,
            inherit: inheritedDefaults
          }
      return {
        ...current,
        form: { ...form, inherit: { ...form.inherit, [field]: checked } }
      }
    })
  }
  const formData = hasNewFormContext ? undefined : sessionState.form.formData
  const inherit = hasNewFormContext
    ? inheritedDefaults
    : sessionState.form.inherit
  const selectedPurposeId = inherit.purposeId
    ? parentPurposeId
    : formData?.purposeId
  const showOkProgramId = isOkProgramPurpose(selectedPurposeId, purposes)
  const uiSchema = planUiSchema({
    okProgramPlaceholder: parentOkProgramText,
    showOkProgramId,
    inherited: inherit
  })

  const validContracts = validPlanContracts(
    contracts,
    formData?.labId,
    inherit.due ? defaultDueDate : formData?.due
  )

  const handleChange = (data: IChangeEvent<CreatePlanFormData>): void => {
    const updated = { ...data.formData }

    if (typeof updated.labId !== 'number') {
      delete updated.labId
    }

    if (typeof updated.okProgramId !== 'number') {
      delete updated.okProgramId
    }

    if (typeof updated.purposeId !== 'number') {
      delete updated.purposeId
    }

    const updatedPurposeId = inherit.purposeId
      ? parentPurposeId
      : updated.purposeId
    if (!isOkProgramPurpose(updatedPurposeId, purposes)) {
      delete updated.okProgramId
    }

    // Auto-suggest title from selected ok-program when title is empty
    if (updated?.okProgramId != null && !updated.description) {
      const selected = okPrograms?.find((r) => r.id === updated.okProgramId)
      if (selected) {
        updated.description = `${selected.number} ${selected.name}`
      }
    }
    updateFormData((current) =>
      retainInheritedPlanDrafts(updated, current, inherit)
    )
  }

  const onSubmit = ({ formData }: IChangeEvent<CreatePlan>): void => {
    if (formData == null || createPlan.isPending || !hasPlacement) return
    setSaveError(undefined)
    const nextPurposeId = inherit.purposeId
      ? parentPurposeId
      : formData.purposeId
    const {
      okProgramId,
      due,
      contractRef: contractRefDraft,
      purposeId,
      ...otherFormValues
    } = formData
    // Placement comes only from root list context; children always inherit it.
    delete otherFormValues.workspaceId
    delete otherFormValues.fagseksjonWorkspaceId
    const nextOkProgramId = isOkProgramPurpose(nextPurposeId, purposes)
      ? okProgramId
      : undefined
    const planNew: CreatePlan = {
      ...otherFormValues,
      ownerId: 'self',
      parentPlans: parentPlanId == null ? [] : [parentPlanId],
      ...(nextOkProgramId == null ? {} : { okProgramId: nextOkProgramId }),
      ...(inherit.due ? {} : { due }),
      ...(inherit.contractRef
        ? {}
        : { contractRef: contractRefDraft?.trim() || null }),
      ...(inherit.purposeId ? {} : { purposeId }),
      ...(parentPlan == null
        ? { workspaceId: divisionId, fagseksjonWorkspaceId: fagseksjonId }
        : {})
    }
    createPlan
      .mutateAsync(planNew)
      .then(async (createdPlan) => {
        Mtds.toast.success('Planen er opprettet')
        if (dismissed.current) return
        setOpen(false)
        try {
          await navigate(`/plans/${createdPlan.id}`)
        } catch (error) {
          showNavigationError(error)
        }
      })
      .catch((error) => {
        setSaveError(error)
      })
  }
  const inheritedValues = {
    due: defaultDueDate,
    contractRef,
    purposeId: parentPurposeText
  }
  const changeInheritance = (
    field: Exclude<PlanField, 'workspaceId'>,
    checked: boolean
  ) => {
    if (!checked) {
      const parentValues = {
        due: parentPlan?.due,
        contractRef: parentPlan?.contractRef,
        purposeId: parentPlan?.purposeId
      }
      updateFormData((current) => ({
        ...current,
        [field]: current?.[field] ?? parentValues[field]
      }))
    }
    updateInheritance(field, checked)
  }
  const inheritanceControls = planInheritanceControls({
    hasParent: parentPlan != null,
    inherited: inherit,
    disabled: {},
    onChange: changeInheritance
  })
  return (
    <ModalDialog
      id='create-plan-modal'
      closedby='any'
      open={isOpen}
      onClose={() => {
        dismissed.current = true
        resetFormSession()
        setOpen(false)
      }}
    >
      <Mtds.Button
        command='close'
        commandfor={'create-plan-modal'}
        aria-label={'Lukk'}
      />
      <Mtds.Prose>
        <Mtds.Heading>Lag ny plan</Mtds.Heading>
        <SaveError error={saveError} action='opprette planen' />
        {parentPlan == null && (
          <Mtds.Grid data-gap='3'>
            <Mtds.Field
              as='select'
              label='Fagseksjon'
              value={fagseksjonId ?? ''}
              disabled={
                contextWorkspace?.kind?.code === 'FAGSEKSJON' ||
                workspaceQuery.isPending
              }
              onChange={(event) =>
                setFagseksjonSelection(Number(event.target.value))
              }
              options={[
                { value: '', label: 'Velg fagseksjon' },
                ...allWorkspaces
                  .filter(
                    (workspace) =>
                      workspace.active && workspace.kind?.code === 'FAGSEKSJON'
                  )
                  .map((workspace) => ({
                    value: String(workspace.id),
                    label: workspace.name
                  }))
              ]}
            />
            <Mtds.Field
              as='select'
              label='Tilsynsdivisjon'
              value={divisionId ?? ''}
              disabled={workspaceQuery.isPending}
              onChange={(event) =>
                setDivisionSelection(Number(event.target.value))
              }
              options={[
                { value: '', label: 'Velg tilsynsdivisjon' },
                ...allWorkspaces
                  .filter(
                    (workspace) =>
                      workspace.active &&
                      workspace.kind?.code === 'TILSYNSDIVISJON'
                  )
                  .map((workspace) => ({
                    value: String(workspace.id),
                    label: workspace.name
                  }))
              ]}
            />
            {workspaceQuery.isError && (
              <Mtds.Alert data-color='danger'>
                Kunne ikke hente arbeidsområder.
              </Mtds.Alert>
            )}
          </Mtds.Grid>
        )}
        {formData?.labId && validContracts?.length === 0 && (
          <Mtds.Alert data-color='warning'>
            Det finnes ingen gyldig kontrakt for valgt laboratorium og frist.
            Opprett en ny kontrakt, eller velg en annen kombinasjon.
          </Mtds.Alert>
        )}
        <Form
          noHtml5Validate
          schema={planSchema({
            defaultDueDate,
            labs: labs ?? [],
            contracts: validContracts ?? [],
            defaultLabId,
            contractId,
            okPrograms: okPrograms ?? [],
            purposes,
            inherited: inherit,
            requiresOkProgram: showOkProgramId
          })}
          uiSchema={withPlanInheritanceControls(
            uiSchema,
            inheritanceControls,
            inherit,
            inheritedValues
          )}
          widgets={widgets}
          templates={templates}
          showErrorList={false}
          validator={validator}
          formData={activePlanFormData(formData, inherit)}
          onChange={(data) => handleChange(data)}
          onSubmit={(data) => onSubmit(data)}
          experimental_defaultFormStateBehavior={{
            constAsDefaults: 'skipOneOf'
          }}
          transformErrors={(errors) =>
            errors.map((error) => {
              if (error.property === '.labId') {
                error.message = 'Du må velge laboratorium for planen'
              } else if (error.property === '.due' && inherit.due) {
                error.message = 'Fristen arves fra overordnet plan'
              }
              return error
            })
          }
        >
          <Mtds.Flex style={{ paddingTop: 'var(--mtds-8)' }}>
            <Mtds.Button
              command='close'
              commandfor={'create-plan-modal'}
              onClick={() => updateFormData(undefined)}
            >
              Forkast
            </Mtds.Button>
            <Mtds.Button
              data-variant='primary'
              type='submit'
              aria-busy={createPlan.isPending}
              disabled={createPlan.isPending || !hasPlacement}
            >
              Opprett
            </Mtds.Button>
          </Mtds.Flex>
        </Form>
      </Mtds.Prose>
    </ModalDialog>
  )
}
